import { logger } from '@salescore/frontend-common'
import { Array, pipe } from 'effect'
import { isUndefined } from 'effect/Predicate'
import { z } from 'zod'

import type { ExpandCursorArea } from '../recoil/cursor'
import type { RecordNodeWithInnerRowIndex } from '../rsheet/util/innerRowRelated'
import { getRecordNodesWithInnerRowIndex } from '../rsheet/util/innerRowRelated'
import type { RSheetColumn, RSheetRecordNode } from './column'

export const placeholderCellValue = {
  __cellValueType: 'placeholder-cell',
} as const

const placeholderCellValueSchema = z.object({
  __cellValueType: z.literal(placeholderCellValue.__cellValueType),
})

type PlaceholderCellValue = z.infer<typeof placeholderCellValueSchema>

export const isPlaceholderCellValue = (value: unknown): value is PlaceholderCellValue =>
  placeholderCellValueSchema.safeParse(value).success

const referenceCellValueSchema = z.object({
  __cellValueType: z.literal('reference'),
  id: z.string(),
  label: z.string(),
  // 参照先エンティティ型の識別子。ペースト時に貼り付け先カラムの参照先と一致する場合のみ id 解決を許可する
  referencedStreamName: z.string(),
})

export type ReferenceCellValue = z.infer<typeof referenceCellValueSchema>

export const isReferenceCellValue = (value: unknown): value is ReferenceCellValue =>
  referenceCellValueSchema.safeParse(value).success

const createReferenceCellValue = (
  column: RSheetColumn<RSheetRecordNode>,
  record: RSheetRecordNode,
  referencedStreamName: string,
): z.ZodSafeParseResult<ReferenceCellValue> =>
  referenceCellValueSchema.safeParse({
    __cellValueType: 'reference',
    // 参照列では value がレコード ID、label が表示用ラベル（useSheetColumns 参照）
    id: column.value(record),
    label: column.label?.(record),
    referencedStreamName,
  })

// eslint-disable-next-line sonarjs/todo-tag
// SALESCORE 内でコピーされた場合のリッチなデータ（TODO: 全ての型でリッチにコピーできるようにする）
const ScCellValueSchema = z.union([placeholderCellValueSchema, referenceCellValueSchema])

export type ScCellValue = z.infer<typeof ScCellValueSchema>

const CellSchema = z.object({
  // value: 外部アプリでコピーされた場合のデータ。参照列では表示用ラベル文字列が入る（クリップボードテキスト・列跨ぎ・外部ペースト用）
  value: z.unknown(),
  salescoreValue: ScCellValueSchema.nullable(),
  height: z.number(),
})

export type Cell = z.infer<typeof CellSchema>
const CellGroupSchema = z.array(CellSchema)
export type CellGroup = z.infer<typeof CellGroupSchema>

// このデータ構造をもとにペースト処理を行う
export const CopyMatrixSchema = z.object({
  rows: z.array(
    z.object({
      columns: z.array(
        z.object({
          cellGroup: CellGroupSchema,
          cellsPerParent: z.array(z.number()),
        }),
      ),
    }),
  ),
})
export type CopyMatrix = z.infer<typeof CopyMatrixSchema>

export const copyMatrixClipboardJsonMimeType = 'application/json' as const
export const copyMatrixClipboardWebJsonMimeType = `web ${copyMatrixClipboardJsonMimeType}` as const

export const CopyMatrixClipboardPayloadSchema = z.object({
  copyMatrix: CopyMatrixSchema,
})
export type CopyMatrixClipboardPayload = z.infer<typeof CopyMatrixClipboardPayloadSchema>

export const toCopyMatrixClipboardPayload = (copyMatrix: CopyMatrix): CopyMatrixClipboardPayload => ({
  copyMatrix,
})

export const isCopyMatrixClipboardPayload = (value: unknown): value is CopyMatrixClipboardPayload =>
  CopyMatrixClipboardPayloadSchema.safeParse(value).success

export interface TransposedCopyMatrix {
  columns: Array<{
    rows: TransposedCopyMatrixRow[]
  }>
}

export interface TransposedCopyMatrixRow {
  cellGroup: Cell[]
  cellsPerParent: number[]
}

// eslint-disable-next-line sonarjs/super-linear-regex
export const normalizeClipboardText = (text: string): string => text.replaceAll('\r', '').replace(/\n+$/v, '')

const clipboardTextToValues = (text: string): string[][] =>
  normalizeClipboardText(text)
    .split('\n')
    .map((x) => x.split('\t')) // 2023/06 Excelなどからコピーするとき、1セルのみのコピーでも末尾に余計な改行が入るので、改行を除去してからsplitする

export const clipboardTextToCopyMatrix = (text: string): CopyMatrix => {
  const values = clipboardTextToValues(text)
  // 複数子ノードには対応していないため、全てのセルグループを1要素にする
  const copyMatrix: CopyMatrix = {
    rows: values.map((row) => ({
      columns: row.map((cell) => ({
        cellGroup: [{ value: cell, salescoreValue: null, height: 1 }],
        cellsPerParent: [1],
      })),
    })),
  }
  return copyMatrix
}

export const copyMatrixToClipboardText = (copyMatrix: CopyMatrix): string => {
  const originalText = copyMatrix.rows
    .map((row) => row.columns.map((column) => cellGroupToClipboardText(column.cellGroup)).join('\t'))
    .join('\n')

  return originalText.length === 0
    ? // 空文字列だと OS が最適化してクリップボードに public.utf8-plain-text が作成されないことがある。
      // Excel ネイティブアプリにペーストすると予期せぬクリップボード要素をペーストしようとしてバグる。
      // Excel ネイティブアプリは "\r\n" を 1 つの空セルとして解釈するので、そうする。
      // 補足 1. "\n" だと Excel で改行として解釈されてしまい、ペースト後にカーソルが下の行に移動してしまうため、"\r\n" にする必要がある。
      // 補足 2.  Google Spreadsheet でも動作確認済み
      '\r\n'
    : originalText
}

const cellGroupToClipboardText = (cellGroup: Cell[]): string =>
  cellGroup.length === 0
    ? ''
    : // eslint-disable-next-line sonarjs/no-nested-conditional
      cellGroup.length === 1 && cellGroup[0] !== undefined
      ? cellValueToClipboardText(cellGroup[0].value)
      : `[${cellGroup.map((cell) => cellValueToClipboardText(cell.value)).join(',')}]`

const cellValueToClipboardText = (value: unknown): string =>
  // eslint-disable-next-line @typescript-eslint/no-base-to-string -- value がオブジェクトの場合に [object Object] と表示される警告だが、 value がオブジェクトの場合は観測されていない（そのうちそのような制約をもった型にしたい）
  value === null || value === undefined || isPlaceholderCellValue(value) ? '' : String(value)

export const sheetAreaToCopyMatrix = (
  records: RSheetRecordNode[],
  columns: Array<RSheetColumn<RSheetRecordNode>>,
  selectedArea: ExpandCursorArea,
  // 参照列の参照先エンティティ型を解決する。クエリツリーを持つ呼出側で注入する（削除経路では () => undefined）
  getReferencedStreamName: (column: RSheetColumn<RSheetRecordNode>) => string | undefined,
): CopyMatrix => {
  const { leftColumnIndex, rightColumnIndex, upRowIndex, downRowIndex, upInnerRowIndex, downInnerRowIndex } =
    selectedArea

  return {
    rows: records.slice(upRowIndex, downRowIndex + 1).map((row, rowIndex) => ({
      columns: columns.slice(leftColumnIndex, rightColumnIndex + 1).map((column) => {
        const nodesForCopy = pipe(
          getRecordNodesWithInnerRowIndex(row, column.node),
          Array.filter(
            (n) =>
              !(rowIndex === 0 && n.innerRowIndexStart < upInnerRowIndex) &&
              !(rowIndex === downRowIndex - upRowIndex && n.innerRowIndexStart >= downInnerRowIndex),
          ),
          Array.filter((n) => !(n.parentRecordNode === undefined && n.recordNode === undefined)),
        )
        return {
          cellGroup: pipe(
            nodesForCopy,
            Array.map((n): Cell => {
              if (n.recordNode === undefined) {
                return {
                  value: placeholderCellValue,
                  salescoreValue: null,
                  height: n.height,
                }
              }
              // ほとんどの場合 label でコピーされる。
              const copiedValue = column.label?.(n.recordNode) ?? column.value(n.recordNode)
              if (column.metaType === 'relation') {
                const referencedStreamName = getReferencedStreamName(column)
                // 参照先エンティティ型が解決できる場合のみリッチコピーする
                if (referencedStreamName !== undefined) {
                  const parsed = createReferenceCellValue(column, n.recordNode, referencedStreamName)
                  if (parsed.success) {
                    return {
                      value: copiedValue ?? null,
                      salescoreValue: parsed.data,
                      height: n.height,
                    }
                  }
                  // パースが失敗したらラベルでのコピーにフォールバックする。
                  // 常に成功する目論見なので失敗したらログを送る
                  logger.warn('SHEET_COPY', 'セルのリッチコピーに失敗しました', parsed)
                }
              }
              return {
                value: copiedValue ?? null,
                salescoreValue: null,
                height: n.height,
              }
            }),
          ),
          cellsPerParent: computeCellsPerParent(nodesForCopy),
        }
      }),
    })),
  }
}

export const transposeMatrix = (copyMatrix: CopyMatrix): TransposedCopyMatrix => {
  const columns: TransposedCopyMatrix['columns'] = []
  for (const row of copyMatrix.rows) {
    for (const [columnIndex, column] of row.columns.entries()) {
      // eslint-disable-next-line security/detect-object-injection -- columnIndex is from trusted Array#entries over row.columns
      columns[columnIndex] ??= { rows: [] }
      // eslint-disable-next-line security/detect-object-injection -- same safe columnIndex as the line above
      columns[columnIndex].rows.push({
        cellGroup: column.cellGroup,
        cellsPerParent: column.cellsPerParent,
      })
    }
  }
  return { columns }
}

const computeCellsPerParent = (nodes: RecordNodeWithInnerRowIndex[]): number[] =>
  pipe(
    nodes,
    Array.reduce(
      { sizes: [] as number[], lastParent: undefined as RSheetRecordNode | undefined },
      ({ sizes, lastParent }, node) => {
        const lastCount = sizes.at(-1)
        if (isUndefined(lastCount)) {
          return { sizes: [1], lastParent: node.parentRecordNode }
        }
        if (node.parentRecordNode === lastParent) {
          return { sizes: [...sizes.slice(0, -1), lastCount + 1], lastParent }
        }
        return { sizes: [...sizes, 1], lastParent: node.parentRecordNode }
      },
    ),
    ({ sizes }) => sizes,
  )
